<template>
  <v-dialog
    v-model="modalStatus"
    :max-width="$vuetify.breakpoint.mobile ? undefined : '820'"
    :fullscreen="$vuetify.breakpoint.mobile"
    scrollable
    persistent
  >
    <v-card class="opening-modal">
      <div class="opening-header">
        <div>
          <p class="opening-title">Crear Folio 1</p>
          <p class="opening-subtitle">Apertura del Libro de Obras · {{ book ? book.name : '-' }}</p>
        </div>
        <v-btn icon dark small :disabled="saving" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="opening-body">
        <v-form ref="form" v-model="valid">
          <v-alert type="info" text dense>
            Este folio quedará reservado como apertura del Libro. La prioridad será Estándar y el responsable será quien lo cree.
          </v-alert>

          <section class="form-section">
            <h3 class="section-title">Datos del proyecto</h3>
            <div class="form-grid">
              <v-text-field v-model="form.project.name" label="Proyecto" outlined dense readonly hide-details="auto" :rules="requiredRule" />
              <v-text-field v-model="form.project.rol_sii" label="Rol SII" outlined dense hide-details="auto" :rules="requiredRule" />
            </div>
            <LocationPicker
              :latitude.sync="form.project.latitude"
              :longitude.sync="form.project.longitude"
              :address.sync="form.project.address"
              label="Dirección del proyecto"
              required
            />
          </section>

          <section class="form-section">
            <h3 class="section-title">Mandante</h3>
            <div class="form-grid">
              <v-text-field v-model="form.mandante.name" label="Nombre o razón social" outlined dense hide-details="auto" :rules="requiredRule" />
              <v-text-field v-model="form.mandante.rut" label="RUT" outlined dense hide-details="auto" :rules="rutRule" />
            </div>
          </section>

          <section class="form-section">
            <h3 class="section-title">Permiso municipal</h3>
            <div class="form-grid">
              <v-text-field v-model="form.permit.number" label="Número de permiso" outlined dense hide-details="auto" :rules="requiredRule" />
              <v-text-field v-model="form.permit.date" label="Fecha" type="date" outlined dense hide-details="auto" :rules="requiredRule" />
            </div>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <h3 class="section-title">Profesionales</h3>
              <v-btn small text color="#2D4B95" @click="addProfessional">
                <v-icon left small>mdi-plus</v-icon>
                Agregar
              </v-btn>
            </div>
            <div v-for="(professional, index) in form.professionals" :key="index" class="professional-row">
              <v-text-field v-model="professional.role" label="Rol" outlined dense hide-details="auto" :rules="requiredRule" />
              <v-text-field v-model="professional.name" label="Nombre" outlined dense hide-details="auto" :rules="requiredRule" />
              <v-text-field v-model="professional.rut" label="RUT" outlined dense hide-details="auto" :rules="rutRule" />
              <v-btn icon small type="button" :disabled="form.professionals.length === 1" @click="removeProfessional(index)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </section>

          <section class="form-section">
            <h3 class="section-title">Previsualización</h3>
            <div class="preview-box">
              <p><b>Proyecto</b>: {{ form.project.name || 'No informado' }}</p>
              <p><b>Dirección</b>: {{ form.project.address || 'No informado' }}</p>
              <p><b>Rol SII</b>: {{ form.project.rol_sii || 'No informado' }}</p>
              <p><b>Mandante</b>: {{ form.mandante.name || 'No informado' }}</p>
              <p><b>Permiso municipal</b>: {{ form.permit.number || 'No informado' }}</p>
              <p><b>Profesionales</b>: {{ professionalSummary }}</p>
            </div>
          </section>

          <section class="form-section">
            <h3 class="section-title">Archivos opcionales</h3>
            <div class="attachments-row">
              <v-btn outlined small type="button" color="#2D4B95" @click="$refs.fileInput.click()">
                <v-icon left small>mdi-paperclip</v-icon>
                Adjuntar archivos
              </v-btn>
              <input ref="fileInput" class="d-none" type="file" multiple @change="setFiles">
              <v-chip v-for="file in form.files" :key="file.name + file.size" small close @click:close="removeFile(file)">
                {{ file.name }}
              </v-chip>
            </div>
          </section>
        </v-form>
      </v-card-text>

      <v-card-actions class="opening-footer">
        <v-spacer />
        <v-btn text type="button" :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn color="#2D4B95" dark type="button" :loading="saving" :disabled="!book" @click="preSubmit">
          <v-icon left small>mdi-publish</v-icon>
          Crear Folio 1
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LocationPicker from '@/components/Globals/Helpers/LocationPicker'

const emptyForm = project => ({
  project: {
    name: project?.name || '',
    address: project?.address || '',
    rol_sii: '',
    latitude: project?.latitude || null,
    longitude: project?.longitude || null,
  },
  mandante: { name: '', rut: '' },
  permit: { number: '', date: '' },
  professionals: [{ role: '', name: '', rut: '' }],
  associations: [],
  files: [],
})

export default {
  name: 'CreateCommunicationFolioOpeningModal',
  components: { LocationPicker },
  props: {
    modalStatus: { type: Boolean, default: false },
    closeModal: { type: Function, required: true },
  },
  data: () => ({
    valid: true,
    form: emptyForm(null),
  }),
  computed: {
    book() {
      const state = this.$store.communicationBooksStore.state
      return state.books.find(book => book.id === state.openingBookId) || null
    },
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    saving() {
      return this.$store.communicationBooksStore.state.saving
    },
    professionalSummary() {
      const names = this.form.professionals
        .map(professional => professional.name)
        .filter(Boolean)
      return names.length ? names.join(', ') : 'No informado'
    },
    requiredRule() {
      return [value => !!String(value || '').trim() || 'Campo obligatorio']
    },
    rutRule() {
      return [
        ...this.requiredRule,
        value => /^[0-9.]+-[0-9kK]$/.test(String(value || '').trim()) || 'RUT inválido',
      ]
    },
  },
  watch: {
    modalStatus(open) {
      if (open) this.resetForm()
    },
  },
  mounted() {
    if (this.modalStatus) this.resetForm()
  },
  methods: {
    resetForm() {
      this.form = emptyForm(this.selectedProject)
    },
    close() {
      if (!this.saving) {
        this.closeModal()
        this.$store.communicationBooksStore.commit('setOpeningBookId', null)
      }
    },
    addProfessional() {
      this.form.professionals.push({ role: '', name: '', rut: '' })
    },
    removeProfessional(index) {
      if (this.form.professionals.length > 1) this.form.professionals.splice(index, 1)
    },
    setFiles(event) {
      this.form.files = this.form.files.concat(Array.from(event.target.files || []))
      event.target.value = ''
    },
    removeFile(file) {
      this.form.files = this.form.files.filter(item => item !== file)
    },
    preSubmit() {
      if (!this.$refs.form.validate()) return
      return this.submit()
    },
    appendNested(formData, prefix, value) {
      if (Array.isArray(value)) {
        value.forEach((item, index) => this.appendNested(formData, `${prefix}[${index}]`, item))
        return
      }
      if (value && typeof value === 'object') {
        Object.keys(value).forEach(key => this.appendNested(formData, `${prefix}[${key}]`, value[key]))
        return
      }
      formData.append(prefix, value === null || value === undefined ? '' : value)
    },
    async submit() {
      if (!this.book) return
      const data = new FormData()
      this.appendNested(data, 'project', this.form.project)
      this.appendNested(data, 'mandante', this.form.mandante)
      this.appendNested(data, 'permit', this.form.permit)
      this.appendNested(data, 'professionals', this.form.professionals)
      data.append('associations', JSON.stringify(this.form.associations))
      this.form.files.forEach(file => data.append('files[]', file))

      try {
        const folio = await this.$store.communicationBooksStore.dispatch('openFolio', {
          bookId: this.book.id,
          projectId: this.selectedProject?.id,
          data,
        })
        if (folio) this.close()
      } catch (error) {
        // The store keeps the request error for the parent module to display.
      }
    },
  },
}
</script>

<style scoped>
.opening-modal { border-radius: 12px; overflow: hidden; }
.opening-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; background: #2D4B95; color: white; }
.opening-title, .opening-subtitle { margin: 0; }
.opening-title { font-size: 16px; font-weight: 700; }
.opening-subtitle { margin-top: 3px; font-size: 12px; opacity: .8; }
.opening-body { max-height: calc(90vh - 130px); padding: 0 20px 20px; }
.form-section { padding: 18px 0; border-bottom: 1px solid #eef1f6; }
.section-heading { display: flex; align-items: center; justify-content: space-between; }
.section-title { margin: 0 0 10px; color: #2D4B95; font-size: 13px; font-weight: 700; }
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.professional-row { display: grid; grid-template-columns: 1fr 2fr 1fr auto; align-items: center; gap: 8px; margin-bottom: 8px; }
.preview-box { padding: 12px; background: #f7f9fd; border: 1px solid #e1e7f2; border-radius: 6px; font-size: 12px; }
.preview-box p { margin: 0 0 5px; }
.preview-box p:last-child { margin-bottom: 0; }
.attachments-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.opening-footer { border-top: 1px solid #eef1f6; }
@media (max-width: 600px) {
  .form-grid, .professional-row { grid-template-columns: 1fr; }
  .professional-row .v-btn { justify-self: end; }
}
</style>
